<!--
  Composant ConceptsCatalog - Catalogue des concepts avec système de filtrage
  
  Ce composant affiche une liste de concepts avec :
  - Barre latérale de filtres (responsive)
  - Liste des concepts avec pagination
  - Gestion des états de chargement et d'erreur
  - Alertes pour les terminologies protégées
-->
<template>
  <div class="concepts-catalog-container concepts-catalog-layout">
    <!-- Barre latérale gauche avec filtres -->
    <aside class="sidebar" style="background-color: var(--gray-100);"
      :class="{ 'sidebar-open': filtersVisibility, 'sidebar-hidden': !shouldShowSidebar }">
      <div class="sidebar-content ">
        <!-- En-tête de la sidebar (visible uniquement sur mobile) -->
        <div class="sidebar-header d-lg-none">
          <h5 class="mb-0">Filtres</h5>
          <button class="btn btn-sm btn-outline-secondary" @click="showFilters()">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <!-- Affichage des filtres de terminologie si des concepts existent -->
        <div v-if="shouldShowSidebar">
          <TerminologyFacet @toggle-selection="onTerminologieSelectionChanged"
            @check-uncheck-all="onTerminologieSelectionChanged" />

          <AlignementFacet @alignement-facets-changed="onAlignementFacetsChanged" />

          <!-- Filtres dynamiques basés sur les terminologies sélectionnées -->
          <TerminologyFilterFacet :key="terminologyFilterFacetKey"
            @terminologie-facets-changed="onTerminologieFacetsChanged" @component-mounted="onFilterComponentMounted" />

        </div>
      </div>
    </aside>

    <!-- Overlay sombre derrière la sidebar en mode mobile -->
    <div class="sidebar-overlay" :class="{ 'active': filtersVisibility && isMobile }" @click="showFilters()"></div>

    <!-- Bouton flottant pour afficher les filtres (mobile/tablette) -->
    <button v-if="(isMobile || isTablet) && conceptsStore.numberOfConcepts > 0"
      class="btn btn-secondary btn-sm rounded-circle ans-filter" @click="showFilters()" type="button">
      <i class="fas fa-filter text-white"></i>
    </button>

    <!-- Contenu principal -->
    <div class="main-content" :class="{ 'with-sidebar': shouldShowSidebar }" ref="conceptsScrollContainer"
      @scroll="handleScroll">
      <v-btn fab small class="concepts-pagination-arrow concepts-pagination-arrow-top" @click="scrollToTop"
        :disabled="isAtTop">
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>

      <div class="content-wrapper">
        <!-- Alerte pour les terminologies à accès restreint -->
        <AlertProtectedTerminologies v-if="notAllowedTerminologies != null && conceptsStore.numberOfConcepts > 0"
          v-bind:notAllowedTerminologies="notAllowedTerminologies" class="alertTerminos" />

        <!-- Squelette de chargement pendant la recherche -->
        <div v-if="conceptsStore.searchLoading" class="ans-mw mx-auto">
          <div class="d-flex flex-column">
            <!-- Génération de 3 cartes skeleton pour simuler le chargement -->
            <div v-for="n in 3" :key="n" class="card mb-3">
              <div class="card-body">
                <div class="placeholder-glow">
                  <span class="placeholder col-7"></span>
                  <span class="placeholder col-4"></span>
                  <span class="placeholder col-4"></span>
                  <span class="placeholder col-6"></span>
                  <span class="placeholder col-8"></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contenu principal une fois le chargement terminé -->
        <div v-else>
          <NoConcepts v-if="conceptsStore.numberOfConcepts == 0 && !hasTerminologiesInFacets" />
          <p v-else-if="!hasSelectedTerminologies" class="text-center mt-5">
            Veuillez sélectionner au moins un filtre
          </p>
          <Concepts v-else @loadmore="setIsAtBottom(false)" />
        </div>
      </div>
      <!-- Bouton pour descendre en bas de la liste -->
      <v-btn fab small color="Gray-700" class="concepts-pagination-arrow concepts-pagination-arrow-bottom"
        @click="scrollToBottom" :disabled="isAtBottom">
        <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
    </div>
  </div>


</template>

<script>
// Imports des stores et composants nécessaires
import ConceptsStore from "../concepts-store";
import AlertProtectedTerminologies from "../components/AlertProtectedTerminologies";
import Concepts from "../components/Concepts";
import NoConcepts from "../components/NoConcepts";
import AlignementFacet from "../components/AlignementFacet";
import TerminologyFacet from "../components/TerminologyFacet";
import TerminologyFilterFacet from "../components/TerminologyFilterFacet";

export default {
  name: 'ConceptsCatalog',

  // Composants utilisés dans ce vue
  components: {
    TerminologyFacet,        // Facette de filtrage par terminologie
    AlignementFacet,
    TerminologyFilterFacet,  // Facette de filtrage des attributs de terminologie
    AlertProtectedTerminologies, // Alerte pour terminologies protégées
    Concepts,                // Liste des concepts
    NoConcepts,             // Message d'absence de résultats
  },

  // Données réactives du composant
  data() {
    /**
     * État local du composant ConceptsCatalog.
     * @returns {Object}
     */
    return {
      conceptsStore: ConceptsStore.state,            // État du store des concepts
      filtersVisibility: false,              // Visibilité de la sidebar de filtres
      notAllowedTerminologies: null,         // Terminologies à accès restreint
      terminologiesList: null,               // Liste complète des terminologies
      windowWidth: window.innerWidth,        // Largeur de la fenêtre pour la responsivité
      isAtTop: true, // Indique si la liste est en haut
      isAtBottom: false, // Indique si la liste est en bas
      terminologyFilterFacetKey: 0,          // Clé pour forcer le re-render du composant TerminologyFilterFacet
      isInitialLoad: true,                   // Flag pour ignorer les appels lors du chargement initial
    };
  },

  // Propriétés calculées pour la gestion responsive et l'affichage
  computed: {
    // Détection des différentes tailles d'écran
    /**
     * Indique si l'affichage est en mode mobile.
     * @returns {boolean}
     */
    isMobile() {
      return this.windowWidth < 768;
    },
    /**
     * Indique si l'affichage est en mode tablette.
     * @returns {boolean}
     */
    isTablet() {
      return this.windowWidth >= 768 && this.windowWidth < 992;
    },
    /**
     * Indique si l'affichage est en mode desktop.
     * @returns {boolean}
     */
    isDesktop() {
      return this.windowWidth >= 992;
    },
    // Détermine si la sidebar doit être affichée
    /**
     * Affiche la sidebar quand au moins une terminologie est disponible.
     * @returns {boolean}
     */
    shouldShowSidebar() {
      return this.conceptsStore.facets.options.all.length > 0;
    },
    // Vérifie si des terminologies sont sélectionnées
    /**
     * Vérifie la présence de terminologies sélectionnées.
     * @returns {boolean}
     */
    hasSelectedTerminologies() {
      return ConceptsStore.hasSelectedTerminologies();
    },
    /**
     * Vérifie si des terminologies sont présentes dans les facettes.
     * @returns {boolean}
     */
    hasTerminologiesInFacets() {
      return ConceptsStore.hasTerminologiesInFacets();
    },
  },

  // Cycle de vie : montage du composant
  /**
   * Initialise l'affichage au montage.
   */
  mounted() {
    window.scrollTo(0, 0);                                // Retour en haut de page
    this.handleResize();                                  // Initialisation responsive
    window.addEventListener('resize', this.handleResize); // Écoute du redimensionnement
  },

  // Cycle de vie : nettoyage avant destruction
  /**
   * Nettoie les écouteurs globaux avant destruction.
   */
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize); // Suppression de l'écouteur
  },

  // Cycle de vie : avant création - chargement des terminologies
  /**
   * Charge les terminologies et prépare la liste des accès restreints.
   */
  async beforeCreate() {
    // Chargement de la configuration des facettes
    await ConceptsStore.getTerminologiesNameFromApi(); // Récupération des terminologies depuis l'API
    this.terminologiesList = this.conceptsStore.terminologiesList;
    // Filtrage des terminologies à accès restreint
    this.notAllowedTerminologies = this.terminologiesList.filter(terminology => !terminology.userHasAccessToConcepts);
  },

  // Méthodes du composant
  methods: {
    /**
     * Bascule la visibilité de la sidebar de filtres
     * Utilisé principalement sur mobile/tablette
     * @return {void}
     */
    showFilters() {
      this.filtersVisibility = !this.filtersVisibility;
    },

    /**
     * Met à jour la largeur de fenêtre pour la gestion responsive
     * Appelée lors du redimensionnement de la fenêtre
     * @return {void}
     */
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    /**
     * Gère le scroll pour activer/désactiver les flèches
     * @return {void}
     */
    handleScroll() {
      const container = this.$refs.conceptsScrollContainer;
      if (!container) return;

      this.isAtTop = container.scrollTop === 0;
      this.isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
    },

    /**
     *  Met à jour l'état isAtBottom pour activer/désactiver la flèche de pagination vers le bas
     * @param value 
     * @return {void}
     */
    setIsAtBottom(value) {
      this.isAtBottom = value;
    },



    /**
     * Fait défiler la liste tout en haut
     * @return {void}
     */
    scrollToTop() {
      const container = this.$refs.conceptsScrollContainer;
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    /**
     * Fait défiler la liste tout en bas
     * @return {void}
     */
    scrollToBottom() {
      const container = this.$refs.conceptsScrollContainer;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    },

    /**
     * Gère l'événement de basculement de sélection d'une terminologie
     * Force le re-render du composant TerminologyFilterFacet pour afficher
     * les facettes correspondant aux nouvelles terminologies sélectionnées
     * @return {void}
     */
    onTerminologieSelectionChanged() {
      // Incrémente la clé pour forcer le re-render de TerminologyFilterFacet
      // Cela permet de recalculer les facets basés sur les terminologies sélectionnées
      this.terminologyFilterFacetKey++;
    },

    /**
     * Gère le changement des facettes de terminologie
     * @return {void}
     */
    onTerminologieFacetsChanged() {
      // Ignorer les appels lors du chargement initial
      if (this.isInitialLoad) {
        return;
      }
      const terminologyIds = this.conceptsStore.facets.options.all
        .filter(option => option.checked === true)
        .map(option => option.id);

      // Réinitialiser la page à 1
      ConceptsStore.setPage(1);

      if (terminologyIds.length === 0) {
        ConceptsStore.resetFacetsList();
        return;
      }

      // Lancer une nouvelle recherche avec les filtres d'attributs
      ConceptsStore.searchConcepts(true, terminologyIds);
    },

    /**
     * Appelé quand le composant TerminologyFilterFacet est monté
     * Marque la fin du chargement initial
     * @return {void}
     */
    onFilterComponentMounted() {
      this.isInitialLoad = false;
    },

    /**
     * Gère le changement de sélection des facettes d'alignement et relance la recherche.
     * @return {void}
     */
    onAlignementFacetsChanged() {
      if (this.isInitialLoad) {
        return;
      }

      const terminologyIds = this.conceptsStore.facets.options.all
        .filter(option => option.checked === true)
        .map(option => option.id);

      ConceptsStore.setPage(1);

      if (terminologyIds.length === 0) {
        ConceptsStore.resetFacetsList();
        return;
      }

      ConceptsStore.searchConcepts(true, terminologyIds);
    },
  }
};
</script>

<style scoped>
/**
 * STYLES DU COMPOSANT CONCEPTS CATALOG
 * 
 * Structure principale :
 * - Layout flexible avec sidebar et contenu principal
 * - Design responsive (mobile/tablette/desktop)
 * - Gestion des états de chargement et filtres
 */

/* === LAYOUT PRINCIPAL === */
.concepts-catalog-layout {
  display: flex;
  background-color: #f8f9fa;
}

/* === SIDEBAR DE FILTRES === */
.sidebar {
  width: 35%;
  /* Largeur fixe sur desktop */
  background: white;
  border-right: 1px solid #e1e5e9;
  /* Bordure de séparation */
  position: fixed;
  /* Position fixe pour le scroll */
  top: 0;
  left: 0;
  height: 90vh;
  /* Hauteur pleine fenêtre */
  overflow-y: auto;
  /* Scroll interne si nécessaire */
  /* Au-dessus du contenu */
  transition: transform 0.3s ease;
  /* Animation d'ouverture/fermeture */
}

.sidebar.sidebar-hidden {
  display: none;
}

.sidebar-content {
  padding: 20px;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #0073aa;
}

.sidebar-header h5 {
  color: #0073aa;
  font-weight: 600;
}

/* === CONTENU PRINCIPAL === */
.main-content {
  max-height: 90vh;
  overflow: auto;
  flex: 1;
  /* Prend l'espace restant */
  transition: margin-left 0.3s ease;
  /* Animation du décalage sidebar */
}

/* Décalage du contenu quand sidebar visible sur desktop */
.main-content.with-sidebar {
  margin-left: 300px;
  /* Correspond à la largeur sidebar */
}

/* Sidebar overlay for mobile */
.sidebar-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sidebar-overlay.active {
  display: block;
  opacity: 1;
}

/* === BOUTON FILTRES MOBILE === */
.ans-filter {
  position: fixed;
  /* Position fixe flottante */
  right: 15px;
  bottom: 15px;
  z-index: 9999;
  /* Au-dessus de tout */
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  /* Ombre portée */
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
}

/* Content styling */
.ans-mw,
.alertTerminos {
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
}

/* === ANIMATION DE CHARGEMENT (SKELETON) === */
.placeholder {
  display: inline-block;
  min-height: 1em;
  vertical-align: middle;
  cursor: wait;
  /* Curseur d'attente */
  background-color: currentColor;
  opacity: 0.5;
  /* Semi-transparent */
}

.placeholder.col-4 {
  width: 33.33%;
}

.placeholder.col-6 {
  width: 50%;
}

.placeholder.col-7 {
  width: 58.33%;
}

.placeholder.col-8 {
  width: 66.66%;
}

.placeholder-glow .placeholder {
  animation: placeholder-glow 2s ease-in-out infinite alternate;
}


/* Style pour les flèches de pagination */
.concepts-pagination-arrow {
  position: absolute;
  right: 20px;
  z-index: 10;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);

  &.concepts-pagination-arrow-top {
    top: 10px;
  }

  &.concepts-pagination-arrow-bottom {
    bottom: 10px;
  }

  &:disabled {
    opacity: 0.3;
  }
}


@keyframes placeholder-glow {
  50% {
    opacity: 0.2;
  }
}

/* === RESPONSIVE DESIGN === */

/* Desktop (≥992px) */
@media (min-width: 992px) {
  .sidebar {
    position: sticky;
    top: 0;
    transform: translateX(0) !important;
  }

  .main-content.with-sidebar {
    margin-left: 0;
  }

  .concepts-catalog-layout {
    display: flex;
  }
}

/* Tablette et mobile (<992px) */
@media (max-width: 991.98px) {
  .sidebar {
    z-index: 1000;
    width: 50% !important;
    transform: translateX(-100%);
    height: 100%;
  }

  .sidebar.sidebar-open {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
    width: 100%;
  }

  .content-wrapper {
    padding: 15px;
  }

  .ans-filter {
    top: 15px;
    left: 15px;
    right: auto;
    bottom: auto;
  }
}

/* Mobile uniquement (<768px) */
@media (max-width: 767.98px) {
  .sidebar {
    width: 100% !important;
    height: 100vh;
    left: 0;
    right: 0;
  }

  .sidebar-content {
    padding: 15px;
  }

  .content-wrapper {
    padding: 10px;
  }
}
</style>
